import { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Target, Award, Flame, Calendar, BookOpen, CheckCircle, Zap, Clock, BarChart3, LogIn, UserPlus } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import { statsApi } from '@/services/api';
import { useAuthStore } from '@/store/useAuthStore';
import { AuthModal } from '@/components/AuthModal';

interface StatsOverview {
  sessions: {
    totalSessions: number;
    completedSessions: number;
    averageScore: number;
    totalTimeSpent: number;
    totalQuestions: number;
    totalCorrect: number;
    accuracy: number;
  };
  gamification: {
    totalXp: number;
    level: number;
    currentStreak: number;
    longestStreak: number;
    xpToNextLevel: number;
  };
  recentActivity: { date: string; sessions: number; score: number }[];
  categoryPerformance: {
    categoryId: string;
    name: string;
    totalQuestions: number;
    correctAnswers: number;
    accuracy: number;
  }[];
}

const DAY_LABELS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
const CATEGORY_COLORS = ['bg-blue-500', 'bg-purple-500', 'bg-orange-500', 'bg-emerald-500', 'bg-pink-500', 'bg-cyan-500'];

function buildWeeklyChart(activity: StatsOverview['recentActivity']) {
  const today = new Date();
  const days: { day: string; score: number; questions: number }[] = [];

  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    const dateKey = date.toISOString().split('T')[0];
    const match = activity.find((a) => String(a.date).startsWith(dateKey));
    days.push({
      day: DAY_LABELS[date.getDay()],
      score: match?.score ?? 0,
      questions: match?.sessions ?? 0,
    });
  }

  return days;
}

export function StatsPage() {
  const { isAuthenticated } = useAuthStore();
  const [overview, setOverview] = useState<StatsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showAuth, setShowAuth] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    statsApi
      .getOverview(7)
      .then(({ data }) => {
        if (!cancelled) {
          setOverview(data);
          setError(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOverview(null);
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const weeklyData = useMemo(
    () => buildWeeklyChart(overview?.recentActivity ?? []),
    [overview?.recentActivity],
  );

  const studyHours = overview ? Math.round(overview.sessions.totalTimeSpent / 3600) : 0;
  const accuracy = overview && overview.sessions.totalQuestions > 0
    ? overview.sessions.accuracy.toFixed(1)
    : '0';

  if (!isAuthenticated) {
    return (
      <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
        <div className="flex-shrink-0 px-4 sm:px-5 pt-5 sm:pt-6 pb-4">
          <h1 className="text-lg sm:text-xl font-bold text-fg">Statistiques</h1>
          <p className="text-xs sm:text-sm text-fg-subtle mt-0.5">Connectez-vous pour voir votre progression</p>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-emerald-500/10 flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-emerald-500/60" />
            </div>
            <p className="text-sm text-fg-subtle mb-1">Aucune statistique disponible</p>
            <p className="text-xs text-fg-faint mb-6 max-w-[260px] mx-auto">Connectez-vous ou créez un compte pour suivre votre progression</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-medium w-full sm:w-auto justify-center"
              >
                <LogIn size={16} />
                Se connecter
              </button>
              <button
                onClick={() => setShowAuth(true)}
                className="flex items-center gap-2 px-5 py-2.5 border border-line text-fg-muted rounded-xl hover:bg-overlay-2 transition-colors text-sm font-medium w-full sm:w-auto justify-center"
              >
                <UserPlus size={16} />
                Créer un compte
              </button>
            </div>
          </div>
        </div>
        {showAuth && <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-app">
        <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
        <div className="flex-shrink-0 px-4 sm:px-5 pt-5 sm:pt-6 pb-4">
          <h1 className="text-lg sm:text-xl font-bold text-fg">Statistiques</h1>
        </div>
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center text-fg-faint">
            <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p className="text-sm">Impossible de charger les statistiques</p>
            <p className="text-xs mt-1 text-fg-faint">Complétez un quiz pour commencer à voir vos données</p>
          </div>
        </div>
      </div>
    );
  }

  const weekSessions = weeklyData.reduce((sum, d) => sum + d.questions, 0);

  return (
    <div className="flex flex-col h-full bg-app pb-20 lg:pb-0">
      <div className="flex-shrink-0 px-4 sm:px-5 lg:px-6 pt-5 sm:pt-6 pb-4 sm:pb-5">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-fg">Statistiques</h1>
          <p className="text-xs sm:text-sm text-fg-subtle mt-0.5">Votre progression</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-5 lg:px-6 pb-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-4 sm:mb-5">
            <QuickStat icon={<Flame className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />} value={`${overview.gamification.currentStreak}`} label="Streak" accent="orange" />
            <QuickStat icon={<Award className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />} value={overview.gamification.totalXp.toLocaleString()} label="Points" accent="yellow" />
            <QuickStat icon={<Target className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />} value={`${accuracy}%`} label="Précision" accent="green" />
            <QuickStat icon={<Clock className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />} value={`${studyHours}h`} label="Étude" accent="blue" />
          </div>

          <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-5 lg:items-start">
            <div className="space-y-4 sm:space-y-5">
              <div className="bg-surface border border-line rounded-2xl p-4 sm:p-5">
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <div>
                    <h2 className="text-xs sm:text-sm font-semibold text-fg">Activité hebdomadaire</h2>
                    <p className="text-[10px] sm:text-[11px] text-fg-faint mt-0.5">
                      {weekSessions > 0 ? `${weekSessions} session${weekSessions > 1 ? 's' : ''} cette semaine` : '7 derniers jours'}
                    </p>
                  </div>
                  <div className="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <BarChart3 className="w-4 h-4 text-emerald-400" />
                  </div>
                </div>
                <div className="h-[140px] sm:h-[180px] lg:h-[200px]">
                  {weeklyData.some((d) => d.score > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={weeklyData} margin={{ top: 8, right: 5, left: -25, bottom: 0 }}>
                        <defs>
                          <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#22c55e" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#22c55e" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 11 }} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: 'rgba(255,255,255,0.2)', fontSize: 9 }} domain={[0, 100]} width={28} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1c232c', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontSize: '12px', color: 'white' }}
                          labelStyle={{ color: 'rgba(255,255,255,0.6)' }}
                          formatter={(value: number) => [`${value}%`, 'Score moyen']}
                          cursor={{ stroke: 'rgba(34,197,94,0.25)', strokeWidth: 1 }}
                        />
                        <Area
                          type="monotone"
                          dataKey="score"
                          stroke="#22c55e"
                          strokeWidth={2.5}
                          fill="url(#scoreGradient)"
                          dot={{ r: 3, fill: '#22c55e', strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: '#22c55e', stroke: '#0d1117', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex flex-col items-center justify-center text-fg-faint gap-2">
                      <BarChart3 className="w-8 h-8 opacity-40" />
                      <p className="text-xs">Aucune activité cette semaine</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-2.5 sm:gap-3">
                <StatCard icon={<BookOpen className="w-4 h-4 sm:w-5 sm:h-5" />} label="Sessions" value={overview.sessions.completedSessions.toString()} gradient="from-blue-500 to-blue-600" />
                <StatCard icon={<CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />} label="Questions" value={overview.sessions.totalQuestions.toString()} gradient="from-emerald-500 to-green-600" />
                <StatCard icon={<Zap className="w-4 h-4 sm:w-5 sm:h-5" />} label="Correctes" value={overview.sessions.totalCorrect.toString()} gradient="from-purple-500 to-violet-600" />
                <StatCard icon={<Calendar className="w-4 h-4 sm:w-5 sm:h-5" />} label="Meilleur streak" value={`${overview.gamification.longestStreak}j`} gradient="from-pink-500 to-rose-600" />
              </div>
            </div>

            <div className="bg-surface border border-line rounded-2xl p-4 sm:p-5 mt-4 sm:mt-5 lg:mt-0">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <h2 className="text-xs sm:text-sm font-semibold text-fg">Performance par catégorie</h2>
                <TrendingUp className="w-4 h-4 text-fg-faint" />
              </div>
              {overview.categoryPerformance.length === 0 ? (
                <p className="text-xs text-fg-faint text-center py-4">Aucune donnée par catégorie</p>
              ) : (
                <div className="space-y-3.5 sm:space-y-4">
                  {overview.categoryPerformance.map((cat, index) => (
                    <div key={cat.categoryId}>
                      <div className="flex items-center justify-between mb-1.5 gap-2">
                        <span className="text-[11px] sm:text-xs text-fg-muted truncate">{cat.name}</span>
                        <span className="text-[11px] sm:text-xs font-semibold text-fg flex-shrink-0">{cat.accuracy}%</span>
                      </div>
                      <div className="h-1.5 bg-overlay-2 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${CATEGORY_COLORS[index % CATEGORY_COLORS.length]} rounded-full transition-all duration-700`}
                          style={{ width: `${cat.accuracy}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const QUICK_STAT_ACCENTS = {
  orange: { bg: 'bg-orange-500/[0.07]', border: 'border-orange-500/10', iconBg: 'bg-orange-500/15', text: 'text-orange-400' },
  yellow: { bg: 'bg-yellow-500/[0.07]', border: 'border-yellow-500/10', iconBg: 'bg-yellow-500/15', text: 'text-yellow-400' },
  green: { bg: 'bg-green-500/[0.07]', border: 'border-green-500/10', iconBg: 'bg-green-500/15', text: 'text-green-400' },
  blue: { bg: 'bg-blue-500/[0.07]', border: 'border-blue-500/10', iconBg: 'bg-blue-500/15', text: 'text-blue-400' },
} as const;

interface QuickStatProps {
  icon: React.ReactNode;
  value: string;
  label: string;
  accent: keyof typeof QUICK_STAT_ACCENTS;
}

function QuickStat({ icon, value, label, accent }: QuickStatProps) {
  const a = QUICK_STAT_ACCENTS[accent];
  return (
    <div className={`${a.bg} ${a.border} rounded-2xl p-3 sm:p-3.5 border transition-colors hover:border-line`}>
      <div className={`w-7 h-7 sm:w-8 sm:h-8 ${a.iconBg} ${a.text} rounded-lg flex items-center justify-center mb-2`}>
        {icon}
      </div>
      <p className="text-sm sm:text-base font-bold text-fg leading-tight">{value}</p>
      <p className="text-[10px] sm:text-[11px] text-fg-faint mt-0.5">{label}</p>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  gradient: string;
}

function StatCard({ icon, label, value, gradient }: StatCardProps) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-3.5 sm:p-4 transition-colors hover:border-line">
      <div className={`w-8 h-8 sm:w-9 sm:h-9 bg-gradient-to-br ${gradient} rounded-lg flex items-center justify-center text-white mb-2.5 sm:mb-3 shadow-lg shadow-black/20`}>
        {icon}
      </div>
      <p className="text-lg sm:text-xl font-bold text-fg">{value}</p>
      <p className="text-[10px] sm:text-[11px] text-fg-faint mt-0.5">{label}</p>
    </div>
  );
}
